import { useState, useRef, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Maximize,
  Minimize,
  Sun,
  Moon,
  Printer,
  ChevronDown,
  ChevronUp,
  MapPin,
  Car,
  Plane,
  ArrowLeft,
  CheckCircle2,
  ZoomIn,
  ZoomOut,
  Edit3
} from 'lucide-react';

export default function MeetingBoardPage() {
  const [searchParams] = useSearchParams();

  // Read URL query parameters
  const paramRef = searchParams.get('ref') || 'SZG-7821';
  const paramName = searchParams.get('name') || 'MR. ALEXANDER WRIGHT';
  const paramFrom = searchParams.get('from') || 'Salzburg Airport Arrivals (SZG)';
  const paramTo = searchParams.get('to') || 'Salzburg City Center / Hotel';
  const paramTime = searchParams.get('time') || 'Today, Scheduled Transfer';
  // const paramFlight = searchParams.get('flight') || 'OS 742';
  const paramVehicle = searchParams.get('vehicle') || 'Mercedes-Benz VIP Saloon';

  const [passengerName, setPassengerName] = useState(paramName.toUpperCase());
  const [isDark, setIsDark] = useState(true);
  const [fontSizeLevel, setFontSizeLevel] = useState<number>(2); // 1: normal, 2: large, 3: extra large
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showInfoDrawer, setShowInfoDrawer] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);

  const boardRef = useRef<HTMLDivElement>(null);

  // Sync state if URL param updates
  useEffect(() => {
    if (paramName) {
      setPassengerName(paramName.toUpperCase());
    }
  }, [paramName]);

  // Handle Fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error('Fullscreen request failed:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const getFontSizeClass = () => {
    switch (fontSizeLevel) {
      case 1:
        return 'text-[38px] sm:text-[54px] md:text-[68px] lg:text-[76px]';
      case 3:
        return 'text-[48px] sm:text-[68px] md:text-[88px] lg:text-[104px]';
      case 2:
      default:
        return 'text-[44px] sm:text-[60px] md:text-[78px] lg:text-[90px]';
    }
  };

  return (
    <div
      className={`min-h-screen flex flex-col transition-colors duration-300 select-none ${
        isDark ? 'bg-[#0A0A0A] text-white' : 'bg-[#F4F4F6] text-[#111827]'
      } print:bg-white print:text-black print:min-h-0`}
    >
      {/* Top Floating Control Bar (Hidden when printing or minimized in clean mode) */}
      <header
        className={`w-full border-b transition-colors px-4 py-3 flex items-center justify-between z-50 print:hidden ${
          isDark ? 'bg-[#141414] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900 shadow-sm'
        }`}
      >
        <div className="flex items-center gap-3">
          <Link
            to="/"
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium hover:bg-white/10 transition"
            title="Return to Website"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Home</span>
          </Link>
          <div className="h-4 w-px bg-gray-500/30 hidden sm:block" />
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-semibold tracking-wider uppercase text-emerald-400">
              Live VIP Board
            </span>
            <span className="text-xs opacity-60 font-mono hidden md:inline">#{paramRef}</span>
          </div>
        </div>

        {/* Right Action Buttons */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* Edit Name Quick Toggle */}
          <button
            onClick={() => setIsEditingName(!isEditingName)}
            className={`p-2 rounded-lg text-xs font-medium flex items-center gap-1 transition ${
              isEditingName
                ? 'bg-[#FF6A1A] text-white'
                : isDark
                ? 'bg-white/10 hover:bg-white/20 text-gray-200'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
            }`}
            title="Edit Passenger Name"
          >
            <Edit3 size={15} />
            <span className="hidden sm:inline">{isEditingName ? 'Done' : 'Edit'}</span>
          </button>

          {/* Text Size Scale */}
          <div
            className={`flex items-center rounded-lg p-0.5 ${
              isDark ? 'bg-white/10' : 'bg-gray-100'
            }`}
          >
            <button
              onClick={() => setFontSizeLevel(prev => Math.max(1, prev - 1))}
              disabled={fontSizeLevel === 1}
              className="p-1.5 hover:opacity-75 disabled:opacity-30"
              title="Decrease Font Size"
            >
              <ZoomOut size={14} />
            </button>
            <span className="text-[11px] font-mono font-bold px-1.5">{fontSizeLevel}x</span>
            <button
              onClick={() => setFontSizeLevel(prev => Math.min(3, prev + 1))}
              disabled={fontSizeLevel === 3}
              className="p-1.5 hover:opacity-75 disabled:opacity-30"
              title="Increase Font Size"
            >
              <ZoomIn size={14} />
            </button>
          </div>

          {/* Dark / Light Toggle */}
          <button
            onClick={() => setIsDark(!isDark)}
            className={`p-2 rounded-lg text-xs font-medium flex items-center gap-1 transition ${
              isDark ? 'bg-white/10 hover:bg-white/20 text-yellow-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
            }`}
            title="Toggle Dark / Light Theme"
          >
            {isDark ? <Sun size={15} /> : <Moon size={15} />}
          </button>

          {/* Print Button */}
          <button
            onClick={handlePrint}
            className="p-2 rounded-lg bg-[#719642] hover:bg-[#5f8034] text-white text-xs font-medium flex items-center gap-1.5 shadow-sm transition"
            title="Print Meeting Board Sign"
          >
            <Printer size={15} />
            <span className="hidden sm:inline">Print</span>
          </button>

          {/* Fullscreen Mode */}
          <button
            onClick={toggleFullscreen}
            className="p-2 rounded-lg bg-[#FF6A1A] hover:bg-[#e0580f] text-white text-xs font-semibold flex items-center gap-1.5 shadow-sm transition"
            title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          >
            {isFullscreen ? <Minimize size={15} /> : <Maximize size={15} />}
            <span className="hidden md:inline">{isFullscreen ? 'Exit' : 'Fullscreen'}</span>
          </button>

          {/* Driver Trip Details Drawer Toggle */}
          <button
            onClick={() => setShowInfoDrawer(!showInfoDrawer)}
            className={`p-2 rounded-lg text-xs font-medium flex items-center gap-1 transition ${
              showInfoDrawer
                ? 'bg-blue-600 text-white'
                : isDark
                ? 'bg-white/10 hover:bg-white/20 text-gray-200'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
            }`}
            title="Toggle Trip Details for Driver"
          >
            {showInfoDrawer ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            <span className="hidden sm:inline">Trip Info</span>
          </button>
        </div>
      </header>

      {/* Driver Info Drawer (Collapsible) */}
      {showInfoDrawer && (
        <aside
          className={`border-b px-6 py-4 animate-in slide-in-from-top-2 duration-200 print:hidden ${
            isDark ? 'bg-[#181818] border-white/10 text-gray-300' : 'bg-white border-gray-200 text-gray-700'
          }`}
        >
          <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-xs font-host">
            {/* <div className="flex items-start gap-2.5">
              <Plane size={16} className="text-[#FF6A1A] shrink-0 mt-0.5" />
              <div>
                <span className="text-gray-400 block font-medium">Flight / Arrival</span>
                <span className="text-sm font-bold text-white dark:text-white font-mono">{paramFlight}</span>
                <span className="text-[11px] block opacity-75">{paramTime}</span>
              </div>
            </div> */}

            <div className="flex items-start gap-2.5">
              <MapPin size={16} className="text-[#719642] shrink-0 mt-0.5" />
              <div>
                <span className="text-gray-400 block font-medium">Pickup & Destination</span>
                <span className="text-xs font-semibold block">{paramFrom}</span>
                <span className="text-xs opacity-75 block">→ {paramTo}</span>
              </div>
            </div>

            <div className="flex items-start gap-2.5">
              <Car size={16} className="text-blue-400 shrink-0 mt-0.5" />
              <div>
                <span className="text-gray-400 block font-medium">Vehicle / Chauffeur</span>
                <span className="text-xs font-semibold block">{paramVehicle}</span>
                <span className="text-[11px] opacity-75 block">Booking Ref: #{paramRef}</span>
              </div>
            </div>

            <div className="flex items-center justify-start sm:justify-end gap-3 pt-2 sm:pt-0">
              <div className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-semibold border border-emerald-500/30">
                <CheckCircle2 size={13} />
                Driver Assigned
              </div>
            </div>
          </div>
        </aside>
      )}

      {/* Main VIP Meeting Board Canvas */}
      <main
        ref={boardRef}
        className="flex-1 flex flex-col items-center justify-between p-6 sm:p-12 md:p-16 max-w-7xl mx-auto w-full text-center relative print:p-8 print:max-w-none print:w-full"
      >
        {/* Top Branding Section */}
        <section className="flex flex-col items-center pt-2 sm:pt-6">
          <div className="relative mb-3">
            <img
              src="/assets/images/logo.png"
              alt="Salzburg Cab Logo"
              className="h-[65px] sm:h-[85px] md:h-[105px] object-contain drop-shadow-md"
            />
          </div>

          <p
            className={`font-host text-[13px] sm:text-[15px] font-bold tracking-[0.35em] uppercase transition-colors ${
              isDark ? 'text-amber-400/90' : 'text-[#719642]'
            }`}
          >
            VIP CHAUFFEUR & PRIVATE TRANSFERS
          </p>
          <p
            className={`font-host text-[10px] sm:text-[12px] tracking-[0.25em] uppercase mt-0.5 ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`}
          >
            SALZBURG • AUSTRIA
          </p>
        </section>

        {/* Center Passenger Welcome Area */}
        <section className="my-auto py-8 sm:py-12 w-full px-4 flex flex-col items-center justify-center">
          {/*<div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-current/20 font-host text-xs sm:text-sm font-semibold tracking-[0.3em] uppercase opacity-90">
            <span>WELCOME TO SALZBURG</span>
          </div>*/}
          <p
            className={`font-host text-[14px] font-medium tracking-[0.25em] uppercase mb-2 ${
              isDark ? 'text-[#FF6A1A]' : 'text-[#719642]'
            }`}
          >
            Welcome
          </p>

          {isEditingName ? (
            <div className="w-full max-w-2xl mx-auto flex flex-col gap-2">
              <input
                type="text"
                value={passengerName}
                onChange={(e) => setPassengerName(e.target.value.toUpperCase())}
                className="text-center font-host text-[30px] sm:text-[44px] font-black uppercase tracking-tight bg-black/20 border-2 border-[#FF6A1A] rounded-xl px-4 py-3 focus:outline-none text-current"
                autoFocus
              />
              <span className="text-xs text-gray-400">Type passenger or organization name and click Done</span>
            </div>
          ) : (
            <h1
              className={`font-host font-black tracking-tight leading-[1.08] uppercase break-words w-full transition-all duration-200 drop-shadow-sm ${getFontSizeClass()} ${
                isDark ? 'text-white' : 'text-gray-950'
              }`}
              style={{
                textShadow: isDark
                  ? '0 4px 20px rgba(0,0,0,0.8), 0 0 40px rgba(255,255,255,0.05)'
                  : '0 2px 10px rgba(0,0,0,0.05)',
              }}
            >
              {passengerName || 'GUEST NAME'}
            </h1>
          )}

          {/* Subtitle / Flight pill if present */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            {/* {paramFlight && (
              <span
                className={`font-host text-xs sm:text-sm font-bold tracking-widest px-4 py-1.5 rounded-full border ${
                  isDark
                    ? 'bg-white/10 border-white/20 text-white'
                    : 'bg-black/5 border-black/10 text-gray-800'
                }`}
              >
                ✈ {paramFlight}
              </span>
            )} */}
            <span
              className={`font-host text-xs sm:text-sm font-semibold tracking-wider px-3.5 py-1.5 rounded-full border ${
                isDark
                  ? 'bg-amber-400/10 border-amber-400/30 text-amber-300'
                  : 'bg-[#719642]/10 border-[#719642]/30 text-[#719642]'
              }`}
            >
              BOOKING #{paramRef}
            </span>
          </div>
        </section>

        {/* Bottom Footer Info Bar */}
        <footer
          className={`w-full pt-6 border-t flex flex-col sm:flex-row items-center justify-between text-xs sm:text-sm font-host gap-3 transition-colors ${
            isDark ? 'border-white/10 text-gray-400' : 'border-gray-300 text-gray-600'
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="font-semibold text-current">Salzburg Cab Premium Fleet</span>
            <span>•</span>
            <span>{paramVehicle}</span>
          </div>

          <div className="flex items-center gap-4">
            <span className="font-medium">
              Dispatch: <a href="tel:+43720775053" className="hover:underline">+43 720 7750 53</a> • <a href="tel:+436763347141" className="hover:underline">+43 676 334 7141</a>
            </span>
            <span className="hidden sm:inline">•</span>
            <a href="mailto:office@salzburgcab.com" className="hidden sm:inline hover:underline font-mono">office@salzburgcab.com</a>
          </div>
        </footer>
      </main>

      {/* Driver Quick Help / Prompt banner at very bottom (hidden on print or fullscreen) */}
      {!isFullscreen && (
        <aside
          className={`py-2 px-4 text-center text-[11px] font-host border-t print:hidden ${
            isDark ? 'bg-black/80 border-white/5 text-gray-400' : 'bg-gray-100 border-gray-200 text-gray-600'
          }`}
        >
          <span>💡 Driver Tip: Click </span>
          <strong className="text-current font-bold">Fullscreen</strong>
          <span> and turn device brightness to 100% for maximum visibility across the arrivals hall.</span>
        </aside>
      )}
    </div>
  );
}
